// ===================================
// DEDICATED ACTIONS PAGE - src/pages/ActionsPage.tsx
// ===================================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { parseUserPermissions } from '../utils/permissions';
import TopicsApi from '../services/topicsApi';
import ActionsApi from '../services/actionsApi';
import { Topic, Action, ActionModalState } from '../types';
import { 
  ArrowLeft, 
  Plus, 
  Settings, 
  Activity,
  Building2,
  Calendar,
  Hash,
  Edit,
  Trash2,
  FileText
} from 'lucide-react';
import Button from '../components/ui/button';
import Badge from '../components/ui/badge';
import ActionsTable from '../components/ActionsTable';
import ActionForm from '../components/ActionForm';
import ActionDetail from '../components/ActionDetail';

const ActionsPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const permissions = parseUserPermissions(user);

  // State
  const [topic, setTopic] = useState<Topic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action modal state
  const [actionModal, setActionModal] = useState<ActionModalState>({
    isOpen: false,
    mode: 'create',
    action: null,
    topicId: null
  });

  // Table refresh trigger
  const [actionsRefreshTrigger, setActionsRefreshTrigger] = useState(0);

  // Load topic data
  useEffect(() => {
    const loadTopic = async () => {
      if (!topicId) {
        setError('Topic ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const topicData = await TopicsApi.getTopic(parseInt(topicId));
        setTopic(topicData);

      } catch (err) {
        const errorInfo = TopicsApi.handleApiError(err);
        setError(errorInfo.message);
        
        // Simple check - assume 404 if topic not found error
        if (errorInfo.message.toLowerCase().includes('not found') || 
            errorInfo.message.toLowerCase().includes('404')) {
          addNotification({
            type: 'error',
            title: 'Topic not found',
            message: 'The requested topic could not be found.'
          });
          navigate('/topics');
        }
      } finally {
        setLoading(false);
      }
    };

    loadTopic();
  }, [topicId, navigate, addNotification]);

  // Handle action CRUD operations
  const handleActionCreate = () => {
    if (!topic) return;
    setActionModal({
      isOpen: true,
      mode: 'create',
      action: null,
      topicId: topic.id
    });
  };

  const handleActionEdit = (action: Action) => {
    if (!topic) return;
    setActionModal({
      isOpen: true,
      mode: 'edit',
      action,
      topicId: topic.id
    });
  };

  const handleActionSelect = (action: Action) => {
    if (!topic) return;
    setActionModal({
      isOpen: true,
      mode: 'view',
      action,
      topicId: topic.id
    });
  };

  // Updated: Now accepts Action object instead of separate IDs
  const handleActionDelete = async (action: Action) => {
    if (!topic) return;

    if (window.confirm(`Are you sure you want to delete the action "${action.action_name}"?`)) {
      try {
        console.log('Deleting action:', action.action_name, 'Topic ID:', topic.id, 'Action ID:', action.id);
        
        await ActionsApi.deleteAction(topic.id, action.id);
        
        addNotification({
          type: 'success',
          title: 'Action deleted',
          message: `Action "${action.action_name}" has been deleted successfully`
        });

        // Close modal if it's open for this action
        if (actionModal.isOpen && actionModal.action?.id === action.id) {
          setActionModal(prev => ({ ...prev, isOpen: false, action: null }));
        }

        // Refresh the actions table
        setActionsRefreshTrigger(prev => prev + 1);

        // Update topic action count
        if (topic) {
          setTopic(prev => prev ? { ...prev, action_count: Math.max(0, prev.action_count - 1) } : null);
        }

      } catch (err) {
        const error = ActionsApi.handleApiError(err);
        addNotification({
          type: 'error',
          title: 'Failed to delete action',
          message: error.message
        });
      }
    }
  };

  const handleActionFormSuccess = (action: Action) => {
    setActionModal(prev => ({ ...prev, isOpen: false }));
    setActionsRefreshTrigger(prev => prev + 1);

    // Update topic action count for new actions
    if (actionModal.mode === 'create' && topic) {
      setTopic(prev => prev ? { ...prev, action_count: prev.action_count + 1 } : null);
    }
  };

  const handleActionModalClose = () => {
    setActionModal(prev => ({ ...prev, isOpen: false }));
  };

  // Handle topic edit (navigate to topics page with edit mode)
  const handleTopicEdit = () => {
    if (!topic) return;
    // Store edit intent in sessionStorage and navigate back
    sessionStorage.setItem('editTopicId', topic.id.toString());
    navigate('/topics');
  };

  // Handle topic delete
  const handleTopicDelete = async () => {
    if (!topic) return;

    if (window.confirm(`Are you sure you want to delete the topic "${topic.name}"? This will also delete all ${topic.action_count} actions in this topic.`)) {
      try {
        await TopicsApi.deleteTopic(topic.id);
        
        addNotification({
          type: 'success',
          title: 'Topic deleted',
          message: `Topic "${topic.name}" has been deleted successfully`
        });

        navigate('/topics');

      } catch (err) {
        const error = TopicsApi.handleApiError(err);
        addNotification({
          type: 'error',
          title: 'Failed to delete topic',
          message: error.message
        });
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        {/* Loading header */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
        
        {/* Loading content */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !topic) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/topics')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Topics
          </Button>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Topic Not Found</h3>
            <p className="text-gray-600 mb-4">{error || 'The requested topic could not be found.'}</p>
            <Button onClick={() => navigate('/topics')}>
              Return to Topics
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const canModifyTopic = permissions.canUpdate;
  const canDeleteTopic = permissions.canDelete;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" onClick={() => navigate('/topics')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Topics
          </Button>
          
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{topic.name}</h1>
              <Badge variant="secondary">
                {topic.action_count} action{topic.action_count !== 1 ? 's' : ''}
              </Badge>
            </div>
            {topic.description && (
              <p className="text-gray-600 mt-1">{topic.description}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {canModifyTopic && (
            <Button variant="outline" size="sm" onClick={handleTopicEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Topic
            </Button>
          )}
          
          {canDeleteTopic && topic.action_count === 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleTopicDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Topic
            </Button>
          )}
          
          {permissions.canCreate && (
            <Button size="sm" onClick={handleActionCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Action
            </Button>
          )}
        </div>
      </div>

      {/* Topic Info Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Topic Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Hash className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Topic ID</p>
              <p className="font-medium text-gray-900">{topic.id}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Building2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Business</p>
              <p className="font-medium text-gray-900">
                {topic.business_id ? `ID ${topic.business_id}` : 'Global'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Calendar className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Last Updated</p>
              <p className="font-medium text-gray-900">
                {new Date(topic.updated_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Management */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">Actions Management</h3>
          </div>
        </div>
        
        <div className="p-6">
          <ActionsTable
            topic={topic}
            refreshTrigger={actionsRefreshTrigger}
            onActionSelect={handleActionSelect}
            onActionEdit={handleActionEdit}
            onActionCreate={handleActionCreate}
            onActionDelete={handleActionDelete}
          />
        </div>
      </div>

      {/* Action Form Modal */}
      {actionModal.isOpen && actionModal.mode !== 'view' && (
        <ActionForm
          isOpen={actionModal.isOpen}
          mode={actionModal.mode}
          topic={topic}
          action={actionModal.action}
          onClose={handleActionModalClose}
          onSuccess={handleActionFormSuccess}
        />
      )}

      {/* Action Detail Modal */}
      {actionModal.isOpen && actionModal.mode === 'view' && actionModal.action && (
        <ActionDetail
          isOpen={actionModal.isOpen}
          action={actionModal.action}
          topic={topic}
          onClose={handleActionModalClose}
          onEdit={() => {
            setActionModal(prev => ({ ...prev, mode: 'edit' }));
          }}
          onDelete={() => {
            if (actionModal.action) {
              handleActionDelete(actionModal.action);
            }
          }}
        />
      )}
    </div>
  );
};

export default ActionsPage;